import type { Workspace, Account } from "./types";
import { seedColor } from "./format";
import type { GraphModel, OrgNode, Contract, Instance, Person, UserAccount, Affiliation } from "./graph";

/* Derive the relationship graph from the live workspace, so Relationships is a
 * lens on the same accounts the rest of the app shows — not a separate dataset.
 *
 * Mapping:
 *   account            → Organization (org.id === account.id, for cross-linking)
 *   account's renewal  → Contract (ARR, term)
 *   deployment record  → Instance (the unit of delivery, with region/seats/uptime)
 *   account contact    → Person (deduped by email → identity resolution)
 *   contact × instance → User (a login per deployment)
 *
 * Two small, declared augmentations keep the "graph, not rows" story the flat
 * account list can't tell on its own: a parent company that owns two accounts
 * (hierarchy roll-up), and a group executive who is a contact on both of them
 * (one person, many orgs). Both are clearly labelled demo relationship data
 * layered on top of the real accounts. */

const TODAY = +new Date("2026-08-20");
const DAY = 86_400_000;
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

// Declared corporate hierarchy: these two accounts are subsidiaries of one group.
export const PARENT_ORG_ID = "grp_panpac";
const PARENT_ORG_NAME = "Pan-Pacific Industrial Group";
const SUBSIDIARIES: Record<string, string> = { a8: PARENT_ORG_ID, a12: PARENT_ORG_ID };

export function buildGraphFromWorkspace(ws: Workspace): GraphModel {
  const accounts = ws.accounts;
  const deployments = ws.records?.deployment ?? [];

  // ---- orgs: the parent group + one per account ----
  const orgs: OrgNode[] = [
    { id: PARENT_ORG_ID, name: PARENT_ORG_NAME, type: "Parent company", color: "#6B7280" },
    ...accounts.map((a): OrgNode => ({
      id: a.id, name: a.name, type: String(a.attributes?.segment ?? a.industry ?? "Customer"),
      color: seedColor(a.name), accountId: a.id, parentOrgId: SUBSIDIARIES[a.id],
    })),
  ];

  // ---- contracts: one subscription per account, from its renewal ----
  const contracts: Contract[] = accounts.map((a): Contract => {
    const daysToRenew = (+new Date(a.renewalDate) - TODAY) / DAY;
    const status: Contract["status"] = a.healthBand === "risk" ? "at risk" : daysToRenew <= 90 ? "renewing" : "active";
    return { id: `ct_${a.id}`, orgId: a.id, name: `${a.name} — Subscription`, revenueType: "subscription", amount: a.arr, amountLabel: "ARR", startDate: a.startDate, endDate: a.renewalDate, status };
  });

  // ---- instances: deployment records (or a synthesized Production instance) ----
  const instances: Instance[] = [];
  for (const a of accounts) {
    const deps = deployments.filter((d) => String(d.attributes.account) === a.id);
    if (deps.length) {
      for (const d of deps) {
        const region = String(d.attributes.region ?? "");
        instances.push({
          id: `inst_${d.id}`, contractId: `ct_${a.id}`, accountId: a.id,
          name: `${a.name} · ${region || "Production"}`, health: a.health,
          metrics: [
            { label: "Region", value: region || "—" },
            { label: "Seats provisioned", value: String(d.attributes.seats_provisioned ?? "—") },
            { label: "Uptime (30d)", value: d.attributes.uptime_30d != null ? `${d.attributes.uptime_30d}%` : "—" },
          ],
        });
      }
    } else {
      instances.push({
        id: `inst_${a.id}_main`, contractId: `ct_${a.id}`, accountId: a.id,
        name: `${a.name} · Production`, health: a.health,
        metrics: [{ label: "Active seats", value: `${a.seatsActive} / ${a.seats}` }, { label: "Region", value: a.region }],
      });
    }
  }
  const instancesForAccount = (aid: string) => instances.filter((i) => i.accountId === aid);

  // ---- people (deduped by email), users (a login per instance), affiliations ----
  const people: Person[] = [];
  const users: UserAccount[] = [];
  const affiliations: Affiliation[] = [];
  const byEmail = new Map<string, Person>();
  let uSeq = 0, afSeq = 0;

  for (const a of accounts) {
    for (const c of a.contacts) {
      let person = byEmail.get(c.email);
      if (!person) {
        person = { id: `p_${slug(c.email)}`, name: c.name, title: c.title, email: c.email, employerOrgId: a.id };
        byEmail.set(c.email, person);
        people.push(person);
      }
      affiliations.push({ id: `af_${afSeq++}`, personId: person.id, targetType: "org", targetId: a.id, role: c.role });
      const active = (c as Account["contacts"][number]).sentiment !== "negative";
      for (const inst of instancesForAccount(a.id)) {
        users.push({ id: `usr_${uSeq++}`, personId: person.id, instanceId: inst.id, role: c.role, active, lastActive: active ? "2026-08-16" : "2026-06-28" });
      }
    }
  }

  // Declared shared contact: a group exec who sponsors both subsidiaries.
  if (accounts.some((a) => a.id === "a8") && accounts.some((a) => a.id === "a12")) {
    const jordan: Person = { id: "p_jordan_ellis", name: "Jordan Ellis", title: "Group VP, Operations", email: "j.ellis@panpac.group", employerOrgId: PARENT_ORG_ID };
    people.push(jordan);
    for (const aid of ["a8", "a12"]) {
      affiliations.push({ id: `af_${afSeq++}`, personId: jordan.id, targetType: "org", targetId: aid, role: "Executive sponsor", note: "Oversees both subsidiaries from the group" });
      for (const inst of instancesForAccount(aid)) {
        users.push({ id: `usr_${uSeq++}`, personId: jordan.id, instanceId: inst.id, role: "Executive sponsor", active: true, lastActive: "2026-08-14" });
      }
    }
  }

  return {
    vocab: { org: "Organization", contract: "Contract", instance: "Deployment", instancePlural: "Deployments", revenue: "ARR" },
    orgs, contracts, instances, people, users, affiliations,
  };
}
